import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Seo, { SITE_URL } from "@/components/feature/Seo";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles, categories } from "@/mocks/gossipData";
import GossipToolbar from "./components/GossipToolbar";

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "吃瓜合集列表",
    description: "按主题分类浏览福利吃瓜大合集、精选盘点与资源包汇总内容。",
    url: `${SITE_URL}/gossip`,
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "吃瓜合集", item: `${SITE_URL}/gossip` },
    ],
  },
];

export default function GossipListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") ?? "all";
  const keyword = searchParams.get("q") ?? "";
  const sort = searchParams.get("sort") ?? "latest";
  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const updateParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (!value) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    setSearchParams(next);
  };

  const kw = keyword.trim().toLowerCase();
  const filtered = articles.filter((item) => {
    const categoryOk = activeCategory === "all" || item.categorySlug === activeCategory;
    const keywordOk =
      !kw ||
      item.title.toLowerCase().includes(kw) ||
      item.summary.toLowerCase().includes(kw) ||
      item.tags.some((tag) => tag.toLowerCase().includes(kw));
    return categoryOk && keywordOk;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sort === "hot") return b.readCount - a.readCount;
    if (sort === "title") return a.title.localeCompare(b.title, "zh-Hans-CN");
    return a.date < b.date ? 1 : -1;
  });

  const activeName =
    activeCategory === "all"
      ? "全部合集"
      : categories.find((item) => item.slug === activeCategory)?.name ?? "全部合集";

  return (
    <>
      <Seo
        title="吃瓜合集列表｜福利吃瓜大合集与精选盘点分类浏览"
        description="按主题分类浏览福利吃瓜大合集、福利吃瓜资源包与精选盘点内容，支持关键词搜索与排序，快速找到你想看的娱乐热点合集。"
        keywords="福利吃瓜大合集,吃瓜合集列表,福利吃瓜资源包,福利吃瓜盘点,娱乐热点合集"
        path="/gossip"
        ogType="website"
        jsonLd={jsonLd}
      />
      <SiteLayout>
        <section className="w-full bg-background-100 px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: "吃瓜合集" }]} />
          <div className="mt-5 flex flex-col gap-3">
            <h1 className="font-heading text-3xl text-foreground-950 md:text-5xl">
              吃瓜合集 · {activeName}
            </h1>
            <p className="max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              这里汇集了福利吃瓜大合集、精选盘点与资源包汇总的完整目录。用分类标签或关键词搜索，几秒钟就能定位到感兴趣的内容。
            </p>
          </div>
        </section>

        <section className="w-full px-4 py-10 md:px-6 md:py-14" aria-label="合集筛选与列表">
          <GossipToolbar
            activeCategory={activeCategory}
            keyword={input}
            sort={sort}
            total={sorted.length}
            onCategoryChange={(slug) =>
              updateParams({ category: slug === "all" ? null : slug })
            }
            onKeywordChange={setInput}
            onSearch={() => updateParams({ q: input.trim() || null })}
            onSortChange={(value) => updateParams({ sort: value })}
          />

          {sorted.length > 0 ? (
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {sorted.map((item) => (
                <ArticleCard key={item.id} article={item} />
              ))}
            </div>
          ) : (
            <div className="mt-10 flex flex-col items-center gap-4 rounded-2xl border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-search-eye-line text-2xl" aria-hidden="true" />
              </span>
              <h2 className="font-heading text-xl text-foreground-950">没有找到匹配的合集</h2>
              <p className="max-w-md text-sm text-foreground-600">
                换个关键词试试，或者切换回全部分类，浏览近期更新的整体内容。
              </p>
              <button
                type="button"
                onClick={() => {
                  setInput("");
                  setSearchParams(new URLSearchParams());
                }}
                className="whitespace-nowrap rounded-full bg-foreground-950 px-5 py-2.5 text-sm font-label text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                重置筛选条件
              </button>
            </div>
          )}
        </section>
      </SiteLayout>
    </>
  );
}